import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import { AddressApi, UsersApi } from "@/config/apiClient";
import { createApiClient } from "@/config/createApiClient";
import { currentWorkspaceAtom } from "@/store";

const apiClient = createApiClient(AddressApi);
const usersApiClient = createApiClient(UsersApi);

export const useAddress = () => {
  const currentWorkspace = useAtomValue(currentWorkspaceAtom);

  const queryClient = useQueryClient();

  const useGetAddress = () => {
    return useQuery({
      queryKey: [
        "addresses",
        currentWorkspace?.accountId,
        currentWorkspace?.organizationId,
        currentWorkspace?.id,
      ],
      enabled: !!currentWorkspace,
      queryFn: () => {
        return apiClient.addressesControllerGetAddresses({
          accountId: currentWorkspace?.accountId,
          organizationId: currentWorkspace?.organizationId,
          workspaceId: currentWorkspace?.id,
        });
      },
    });
  };

  const useAddAddress = () => {
    return useMutation({
      mutationKey: ["addAddress"],
      mutationFn: (
        body: Parameters<
          typeof apiClient.addressesControllerCreateAddress
        >["0"]["addressCreateCommand"]
      ) =>
        apiClient.addressesControllerCreateAddress({
          accountId: currentWorkspace?.accountId,
          organizationId: currentWorkspace?.organizationId,
          workspaceId: currentWorkspace?.id,
          addressCreateCommand: body,
        }),
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ["addresses"] }),
          queryClient.invalidateQueries({
            queryKey: ["getUserDefaultAddress"],
          }),
        ]);
      },
    });
  };

  const useUpdateAddress = () => {
    return useMutation({
      mutationKey: ["updateAddress"],
      mutationFn: (
        body: Parameters<
          typeof apiClient.addressesControllerUpdateAddress
        >["0"]["addressUpdateCommand"]
      ) => {
        return apiClient.addressesControllerUpdateAddress({
          addressId: body.id,
          accountId: currentWorkspace?.accountId,
          organizationId: currentWorkspace?.organizationId,
          workspaceId: currentWorkspace?.id,
          addressUpdateCommand: body,
        });
      },
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ["addresses"] }),
          queryClient.invalidateQueries({
            queryKey: ["getUserDefaultAddress"],
          }),
        ]);
      },
    });
  };

  const useDeleteAddress = () => {
    return useMutation({
      mutationKey: ["deleteAddress"],
      mutationFn: (
        addressDeleteCommand: Parameters<
          typeof apiClient.addressesControllerDeleteAddress
        >["0"]["addressDeleteCommand"]
      ) =>
        apiClient.addressesControllerDeleteAddress({
          accountId: currentWorkspace?.accountId,
          organizationId: currentWorkspace?.organizationId,
          workspaceId: currentWorkspace?.id,
          addressId: addressDeleteCommand.id,
          addressDeleteCommand,
        }),
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ["addresses"] }),
          queryClient.invalidateQueries({
            queryKey: ["getUserDefaultAddress"],
          }),
        ]);
      },
    });
  };

  const useSetDefaultAddress = () => {
    return useMutation({
      mutationKey: ["setDefaultAddress"],
      mutationFn: (
        body: Parameters<
          typeof usersApiClient.usersControllerSetDefaultAddress
        >["0"]["defaultAddressByUserSetCommand"]
      ) =>
        usersApiClient.usersControllerSetDefaultAddress({
          accountId: currentWorkspace?.accountId,
          organizationId: currentWorkspace?.organizationId,
          workspaceId: currentWorkspace?.id,
          defaultAddressByUserSetCommand: body,
        }),
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: ["getUserDefaultAddress"],
        });
      },
    });
  };

  const useGetDefaultAddress = () => {
    return useQuery({
      queryKey: [
        "getUserDefaultAddress",
        currentWorkspace?.accountId,
        currentWorkspace?.organizationId,
        currentWorkspace?.id,
      ],
      enabled: !!currentWorkspace,
      queryFn: () =>
        usersApiClient.usersControllerGetDefaultAddress({
          accountId: currentWorkspace?.accountId,
          organizationId: currentWorkspace?.organizationId,
          workspaceId: currentWorkspace?.id,
        }),
    });
  };

  return {
    useGetAddress,
    useGetDefaultAddress,
    useSetDefaultAddress,
    useAddAddress,
    useUpdateAddress,
    useDeleteAddress,
  };
};
